/* eslint-disable complexity */
/* eslint-disable max-statements */

import type { AdEnginePlugin, AuctionData, Hooks } from "./AdEnginePlugin";
import type { AdNodeManager } from "./AdNodeManager";
import type { GptWrapper } from "./GptWrapper";
import type { MonetizationTierEngine } from "./MonetizationTierEngine";
import {
  type PerformanceMarkRefreshMetadata,
  PerformanceMarkName,
} from "common-types";
import { runHook } from "./runHook";
import { AdNodeRenderStatus, type AdNode } from "./AdNode";
import { waitFor, waitForAllSettled } from "utils";
import type { InstantConfigRepository } from "instant-config";

export type AdEngineConfig = Pick<
  InstantConfigRepository,
  | "icAdEngineBidderInitTimeoutMS"
  | "icAdEngineOnCreateBiddersRequestsTimeoutMS"
  | "icAdEngineWaitBeforeFirstAdsRequestMS"
  | "icTieredMonetizationEnabled"
>;

/**
 * Core AdEngine object that contains the externally accessible managers as well
 * as core api functions for consistence across the application. AdEngine is
 * responsible for:
 *
 * - Requesting AdNodes to be rendered ({@link requestRender})
 * - Requesting and running header bidding auctions ({@link requestBids})
 * - Providing access to the core managers; {@link AdNodeManager} and
 *   {@link GptWrapper} ({@link AdEngine.adNodeManager} and
 *   {@link AdEngine.gptWrapper}).
 *
 * Access to the AdEngine is provided to plugins through the `onAdEngineInit`
 * hook and some hooks that run after the AdEngine is initialized.
 *
 * ## Usage
 *
 * ### Getting Access to the AdEngine, GptWrapper, and AdNodeManager
 *
 * The AdEngine object is passed to the `onAdEngineInit` hook, which is called
 * when the AdEngine class is first initialized and the engine is ready to start
 * bidding and rendering ads.
 *
 * ```ts
 * import { AdEnginePlugin } from "ad-engine";
 *
 * class MyPlugin extends AdEnginePlugin {
 *   private adEngine?: AdEngine; hooks = {
 *     onAdEngineInit: (adEngine) => {
 *       this.adEngine = adEngine;
 *       adEngine.gptWrapper.getAllTargeting(); adEngine.adNodeManager.getAdNodes();
 *       adEngine.adNodeManager.getSlotConfigMap();
 *     }
 *   }
 * }
 * ```
 *
 * ### Requesting AdNodes to be rendered
 *
 * To request an AdNode to be rendered, including running a header bidding
 * auction, call the {@link requestRender} function on the AdEngine, passing in
 * the list of AdNodes to be rendered. This will run an auction for the slots and
 * then render the ads with the GptWrapper.
 *
 * ```ts
 * const leaderboard = adEngine.adNodeManager.getAdNodeByName("top_leaderboard");
 * const boxad = adEngine.adNodeManager.getAdNodeByName("top_boxad");
 * adEngine.requestRender([leaderboard, boxad]);
 * ```
 *
 * ### Requesting Header Bidding Auction Only
 *
 * For some slots, like video slots, you may not want to run the entire render
 * cycle, but just the header bidding process with all bidders. In this case,
 * you can call {@link requestBids} on the AdEngine, passing in the list of
 * AdNodes to bid on.  This will run an auction with all bidders and resolve
 * when all the bids are received or the
 * {@link InstantConfigRepository.icAdEngineOnCreateBiddersRequestsTimeoutMS} is
 * reached.
 *
 * ```ts
 * const adNode = adEngine.adNodeManager.getAdNodeByName("featured");
 * await adEngine.requestBids([adNode]);
 * ```
 *
 * ### Disabling Header Bidding
 *
 * In some cases, header bidding will need to be disabled for the current page
 * load. This can be done by calling {@link disableBidding} on the AdEngine. For
 * all subsequent renders or calls to {@link requestBids}, the bidding will be
 * skipped.
 *
 * if {@link disableBidding} is called during the `onAdEngineInit` hook, then
 * when {@link start} is called, the bidders will not be initialized and the
 * `onBiddersInit` hook will not be called. All render requests including the
 * first render cycle will skip the bidding process.
 */
export class AdEngine {
  private enableBidding = true;
  /**
   * Count of render cycles that have been run. The first cycle will be 1.
   */
  private renderCycleCount = 0;

  /**
   * Flag to indicate if the first render cycle is complete. This is used to
   * ensure that all subsequent render cycles wait for the first render cycle
   * to complete before requesting more slots. We need to wait for this to allow
   * GAM to process the first request and determine if any master/companion
   * takeovers are to be rendered.
   */
  private isFirstRenderCycleComplete = false;

  constructor(
    private readonly config: AdEngineConfig,
    private readonly plugins: AdEnginePlugin[],
    public readonly gptWrapper: GptWrapper,
    public readonly adNodeManager: AdNodeManager,
    public readonly monetizationTierEngine: MonetizationTierEngine,
  ) {}

  /**
   * Waits for the first render cycle to complete. This will check every 50ms up to
   * 50 times (2.5 seconds) before resolving to false to allow the render cycle to
   * continue. This wait is used to ensure that Master/Companion takeovers are allowed
   * to render before any subsequent render cycles are requested, however, after the
   * max tries if the first request has not resolved, then keep going to show something.
   *
   * @returns Resolves with true when the first render cycle is complete. If the
   * waitFor times out trying to resolve, it will resolve with false.
   */
  private async waitForFirstRenderCycleComplete(): Promise<boolean> {
    if (this.isFirstRenderCycleComplete) {
      return true;
    }

    return await waitFor(() => this.isFirstRenderCycleComplete, {
      maxTries: 50,
      fixedTimeout: 50,
    });
  }

  /**
   * Disables all header bidding for the current page load. When disabled, any
   * calls to {@link requestBids} will resolve immediately without making any
   * requests. If this is disabled during initialization, the {@link start}
   * method will also skip the `onBiddersInit` hooks and no bidding will be
   * initialized.
   */
  disableBidding() {
    this.enableBidding = false;
  }

  /** @typedef {import("./AdEnginePlugin").Hooks} Hooks */
  /**
   * Starts the AdEngine by initializing bidders then setting up the intersection
   * observer on the {@link AdNodeManager} which will gather the in view nodes and
   * first call nodes and start rendering them.
   *
   * This method will emit the following hooks:
   * - {@link AdEnginePlugin} Hooks.onBiddersInit to initialize the bidders
   *
   * If {@link disableBidding} is called before this method, no bidding will be
   * initialized and the {@link onBiddersInit} hook will not be called. The
   * header bidding auction will also be skipped and any subsequence calls to
   * {@link requestBids} will resolve immediately without making any requests.
   * Once disabled, only google ad manager requests will be made.
   */
  async start() {
    if (this.enableBidding) {
      /*
       * Initialize Bidders. This will run the onBiddersInit hook first to allow each bidder to
       * initialize before we start creating bid requests.
       */
      const initPromises: Promise<void>[] = [];
      runHook(this.plugins, "onBiddersInit", this, initPromises);
      try {
        await waitForAllSettled(
          initPromises,
          this.config.icAdEngineBidderInitTimeoutMS,
          "onBiddersInit",
        );
      } catch (error) {
        console.warn("onBiddersInit timed out", error);
      }
      performance.mark(PerformanceMarkName.AE_ON_BIDDERS_INIT);
    }

    // Request the first call slots and observe the intersection of the adNodes,
    // so we can start rendering them when they come into view.
    this.adNodeManager.requestFirstCallSlotsAndObserveIntersection(this);
  }

  /**
   * Requests header bidding for the provided adNodes. This will call the hook
   * {@link AdEnginePlugin} Hooks.onCreateBiddersRequests and then wait for all
   * the requests are completed or the
   * {@link InstantConfigRepository.icAdEngineOnCreateBiddersRequestsTimeoutMS}
   * is reached.
   *
   * This function will not run if {@link disableBidding} is called before this
   * method.
   *
   * @param adNodes The adNodes to request header bidding for.
   * @returns Resolves when the auction is complete or the
   * {@link InstantConfigRepository.icAdEngineOnCreateBiddersRequestsTimeoutMS} is reached.
   */
  async requestBids(
    adNodes: AdNode[],
    renderCycleCount?: number,
  ): Promise<void> {
    // still return an auctionId so we can uniquely identify this
    // call to requestBids for differentiating it for tracking purposes
    if (!this.enableBidding) {
      return;
    }

    const auctionData: AuctionData = {
      auctionId: crypto.randomUUID(),
      adNodes,
      pageTargeting: this.gptWrapper.getAllTargeting(),
      renderCycleCount,
    };

    const onCreateBidderAdUnitCallback = (
      ...args: Parameters<NonNullable<Hooks["onCreateBidderAdUnit"]>>
    ) => {
      runHook(this.plugins, "onCreateBidderAdUnit", ...args);
    };

    try {
      const biddersRequests: Promise<void>[] = [];

      runHook(
        this.plugins,
        "onCreateBiddersRequests",
        biddersRequests,
        auctionData,
        this,
        onCreateBidderAdUnitCallback,
      );

      performance.mark(PerformanceMarkName.AE_AUCTION_START, {
        detail: {
          renderCycleCount,
          auctionId: auctionData.auctionId,
        } as PerformanceMarkRefreshMetadata,
      });

      await waitForAllSettled(
        [...biddersRequests],
        this.config.icAdEngineOnCreateBiddersRequestsTimeoutMS,
        `onCreateBiddersRequests [cycle ${renderCycleCount}]`,
      );

      performance.mark(PerformanceMarkName.AE_AUCTION_END, {
        detail: {
          renderCycleCount,
          auctionId: auctionData.auctionId,
        } as PerformanceMarkRefreshMetadata,
      });
    } catch (error) {
      console.warn("Bidders requests timed out", error);
    }
  }

  /**
   * Requests the adNodes to be rendered. This will run an auction for the slots
   * and then render the ads with the GptWrapper.
   *
   * On the first render cycle, this will also run the the following hooks in this
   * order:
   * - {@link AdEnginePlugin} Hooks.onCreateBiddersRequests
   * - {@link AdEnginePlugin} Hooks.onBeforeFirstAdsRequest
   *
   * @param adNodes The adNodes to request to be rendered.
   */
  async requestRender(adNodes: AdNode[] = [], forceRender = false) {
    // Filter the adNodes to only those eligible to render
    const eligibleAdNodes: AdNode[] = [];
    for (const adNode of adNodes) {
      if (forceRender || adNode.isEligibleToRender()) {
        adNode.startRendering();
        eligibleAdNodes.push(adNode);
      }
    }

    // Don't run render cycle if no adNodes are passed in or if no adNodes are
    // eligible for rendering
    if (!eligibleAdNodes.length) {
      return;
    }

    // Increment the render cycle count and save as local variable since we
    // await and it might change during the cycle
    this.renderCycleCount++;
    const currentRenderCycleCount = this.renderCycleCount;

    await this.requestBids(eligibleAdNodes, currentRenderCycleCount);

    // Send out the onBeforeFirstCallAdsRequest hook only once, on the first render cycle.
    if (currentRenderCycleCount === 1) {
      // Introduce artificial wait for the first render cycle to allow
      // third party scripts to load and do their thing before we
      // start requesting slots.
      if (this.config.icAdEngineWaitBeforeFirstAdsRequestMS > 0) {
        await new Promise((resolve) =>
          setTimeout(
            resolve,
            this.config.icAdEngineWaitBeforeFirstAdsRequestMS,
          ),
        );
      }

      runHook(
        this.plugins,
        "onBeforeFirstCallAdsRequest",
        this.gptWrapper,
        this.adNodeManager,
      );
      performance.mark(PerformanceMarkName.AE_BEFORE_FIRST_CALL_ADS_REQUEST);
    }

    // if this is not first render cycle, and the first cycle isn't complete yet, then wait
    // until the first render cycle is complete before requesting more slots
    if (currentRenderCycleCount > 1) {
      await this.waitForFirstRenderCycleComplete();
    }

    this.gptWrapper.requestSlots(eligibleAdNodes);

    if (currentRenderCycleCount === 1) {
      // Wait for the first slot in the first render cycle to have the render status change
      // to RENDERED, which happens onSlotRenderEnded event. If we don't get the change in
      // render status after 2.5 seconds (50 tries of 50ms each), we will continue and just
      // render the ads anyway. This should be enough time to get the GAM request back and
      // in rare cases where it takes longer, we will still render the ads eventually.
      await waitFor(
        () =>
          eligibleAdNodes[0].getRenderStatus() === AdNodeRenderStatus.RENDERED,
        { maxTries: 50, fixedTimeout: 50 },
      );
      this.isFirstRenderCycleComplete = true;
    }
  }
}
